import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { I18nProvider } from '@lingui/react';

import { dynamicActivate, i18n } from '../i18n';
import { DEFAULT_LOCALE, detectLocale, normalizeLocale, stripLocaleQueryParam, SupportedLocale } from '../i18n/locales';
import getStorage from '../utils/storage.utils';

const STORAGE_KEY = 'app.locale';
const storage = getStorage();

// Where the active locale was resolved from on this load. Consumed by the
// server-sync bridge to decide whether a querystring override should win over
// the user's server-persisted preference (see locale-user-sync.tsx), and by
// LocationLocaleSync to decide whether it may steer the language by location.
//
//   query    — ?lang / ?locale link override; wins and is pushed to the server.
//   stored   — an explicit choice: the language toggle, or a value adopted from
//              the user's server `preferredLocale`.
//   device   — the device language said Spanish. Not an explicit in-app choice,
//              so the server preference still wins, but location must NOT
//              override it: a Spanish device gets Spanish wherever it is.
//   detected — a geography-derived default (PR → Spanish, else English). The
//              weakest source; LocationLocaleSync keeps steering it.
export type LocaleSource = 'query' | 'stored' | 'device' | 'detected';

interface LocaleContextValue {
  locale: SupportedLocale;
  /**
   * Change the active locale. `persist: false` applies a location-derived
   * DEFAULT (see LocationLocaleSync) without writing storage or marking an
   * explicit preference, so the language keeps following the location waterfall
   * and re-detects next visit. Any other call (toggle, server adopt) persists
   * and marks the source `stored`, after which the default no longer steers it.
   */
  setLocale: (locale: SupportedLocale, opts?: { persist?: boolean }) => Promise<void>;
  source: LocaleSource;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const syncDocumentLang = (locale: SupportedLocale) => {
  document.documentElement.lang = locale;
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<SupportedLocale>(DEFAULT_LOCALE);
  const [source, setSource] = useState<LocaleSource>('detected');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const detected = detectLocale();
      // normalizeLocale migrates the legacy `es` value older clients persisted.
      const stored = normalizeLocale(await storage.getItem(STORAGE_KEY));

      // Querystring always wins and persists as the user's new preference.
      // Otherwise stored preference. Otherwise auto-detected (Spanish device
      // language → es-PR, else PR → es-PR, else en), which we do NOT persist so
      // re-detection follows a changed device language on the next visit.
      let initial: SupportedLocale;
      let initialSource: LocaleSource;
      let persistInitial = false;
      if (detected.fromQuery) {
        initial = detected.locale;
        initialSource = 'query';
        persistInitial = true;
      } else if (stored) {
        initial = stored;
        initialSource = 'stored';
        // Re-persist so a migrated legacy value (es → es-PR) is normalized on disk.
        persistInitial = stored !== (await storage.getItem(STORAGE_KEY));
      } else {
        initial = detected.locale;
        initialSource = detected.fromLanguage ? 'device' : 'detected';
      }

      await dynamicActivate(initial);
      if (persistInitial) await storage.setItem(STORAGE_KEY, initial);
      stripLocaleQueryParam();

      if (cancelled) return;
      syncDocumentLang(initial);
      setLocaleState(initial);
      setSource(initialSource);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setLocale = useCallback(async (next: SupportedLocale, opts?: { persist?: boolean }) => {
    const persist = opts?.persist ?? true;
    await dynamicActivate(next);
    if (persist) await storage.setItem(STORAGE_KEY, next);
    syncDocumentLang(next);
    setLocaleState(next);
    // An explicit change becomes a 'stored' preference; a non-persisting default
    // stays 'detected' so LocationLocaleSync keeps steering it by location.
    setSource(persist ? 'stored' : 'detected');
  }, []);

  if (!ready) return null;

  return (
    <LocaleContext.Provider value={{ locale, setLocale, source }}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
};
